import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wifi, Plus, Trash2, Star, ExternalLink, AlertCircle, CheckCircle, Clock, XCircle,
  Phone, Eye, EyeOff, Copy, RefreshCw, Pause, Play,
} from 'lucide-react';
import { integrationsApi } from '@/api/integrations';
import { telephonyApi, type GlobalmaxConnection } from '@/api/telephony';
import { Badge, Loading } from '@/components/ui';
import { cn } from '@/utils/helpers';
import type { OrgWhatsappAccount } from '@/types';
import toast from 'react-hot-toast';
import OrgEmailChannelSection from './OrgEmailChannelSection';

// ── Status config ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; icon: React.ReactNode; badge: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  active:       { label: 'פעיל',         icon: <CheckCircle className="h-4 w-4" />, badge: 'success' },
  pending_scan: { label: 'ממתין לסריקה', icon: <Clock className="h-4 w-4" />,        badge: 'warning' },
  disconnected: { label: 'מנותק',        icon: <AlertCircle className="h-4 w-4" />,  badge: 'danger'  },
  inactive:     { label: 'לא פעיל',      icon: <XCircle className="h-4 w-4" />,     badge: 'neutral' },
};

const GLOBALMAX_STATUS: Record<string, { label: string; badge: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  active:    { label: 'פעיל',    badge: 'success' },
  pending:   { label: 'ממתין',   badge: 'warning' },
  suspended: { label: 'מושהה',   badge: 'neutral' },
  failed:    { label: 'נכשל',    badge: 'danger'  },
};

// ── Provision Modal ────────────────────────────────────────────────────────────

function ProvisionModal({ orgId, onClose, onDone }: { orgId: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');

  const mutation = useMutation({
    mutationFn: () => integrationsApi.provisionGreenApiInstance(orgId, name),
    onSuccess: () => { toast.success('מספר WhatsApp נוצר בהצלחה'); onDone(); onClose(); },
    onError: (e: Error) => toast.error(`שגיאה: ${e.message}`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-2xl shadow-2xl w-full max-w-sm p-6 mx-4" onClick={(e) => e.stopPropagation()} dir="rtl">
        <h2 className="text-lg font-bold text-white mb-4">הוסף מספר WhatsApp</h2>
        <div className="mb-5">
          <label className="block text-xs font-medium text-slate-400 mb-1">שם מזהה</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="למשל: WhatsApp שיווק"
            dir="rtl"
            className="w-full h-9 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-gold/30"
          />
          <p className="text-[11px] text-slate-500 mt-1.5">יצור instance חדש ב-GreenAPI Partner ויחבר לארגון</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">ביטול</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || mutation.isPending}
            className="px-5 py-2 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'יוצר...' : 'צור מספר'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── WhatsApp Account Card ──────────────────────────────────────────────────────

function WhatsAppCard({ account, orgId, onRefresh }: { account: OrgWhatsappAccount; orgId: string; onRefresh: () => void }) {
  const queryClient = useQueryClient();
  const status = STATUS_CONFIG[account.status] ?? STATUS_CONFIG.inactive;

  const setDefaultMutation = useMutation({
    mutationFn: () => integrationsApi.upsert({
      organization_id: orgId,
      provider: 'whatsapp_default',
      config: { default_account_id: account.id },
      is_active: true,
    } as Parameters<typeof integrationsApi.upsert>[0]),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['whatsapp_accounts_admin', orgId] }); toast.success('ברירת מחדל עודכנה'); },
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.deleteWhatsappAccount(orgId, account.id),
    onSuccess: () => { onRefresh(); toast.success('חשבון נמחק'); },
    onError: () => toast.error('שגיאה במחיקה'),
  });

  return (
    <div className={cn(
      'bg-navy-dark border rounded-xl p-4 flex items-start gap-3 transition-colors',
      account.is_default_reply ? 'border-gold/40' : 'border-navy-border'
    )}>
      <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center text-white flex-shrink-0',
        account.status === 'active' ? 'bg-green-600' : account.status === 'pending_scan' ? 'bg-amber-500' : 'bg-slate-600'
      )}>
        <Wifi className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-semibold text-white text-sm">{account.name}</span>
          {account.is_default_reply && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-gold bg-gold/10 border border-gold/20 rounded-full px-2 py-0.5">
              <Star className="h-2.5 w-2.5 fill-current" /> ברירת מחדל
            </span>
          )}
          <Badge variant={status.badge}>{status.label}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="font-mono num">{account.phone_number || '—'}</span>
          <span className="font-mono num text-slate-600">ID: {account.instance_id}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!account.is_default_reply && (
          <button onClick={() => setDefaultMutation.mutate()} disabled={setDefaultMutation.isPending}
            title="הגדר כברירת מחדל" className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-gold hover:bg-gold/10 transition-colors">
            <Star className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={() => { if (confirm(`למחוק "${account.name}"?`)) deleteMutation.mutate(); }}
          disabled={deleteMutation.isPending} title="מחק"
          className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Globalmax helpers ──────────────────────────────────────────────────────────

function maskToken(t: string | null | undefined): string {
  if (!t || t.length < 8) return '•••';
  return `${t.slice(0, 4)}${'•'.repeat(6)}${t.slice(-3)}`;
}

// ── Globalmax Connect Modal ────────────────────────────────────────────────────

function GlobalmaxConnectModal({
  orgId, onClose, onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: (conn: GlobalmaxConnection) => void;
}) {
  const [did, setDid] = useState('');
  const [customerId, setCustomerId] = useState('');

  const mutation = useMutation({
    mutationFn: () => telephonyApi.createGlobalmax(orgId, {
      did_number: did.trim() || undefined,
      globalmax_customer_id: customerId.trim() || undefined,
    }),
    onSuccess: (conn) => { toast.success('חיבור Globalmax נוצר'); onCreated(conn); onClose(); },
    onError: (e: Error) => toast.error(`שגיאה: ${e.message}`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-2xl shadow-2xl w-full max-w-sm p-6 mx-4" onClick={(e) => e.stopPropagation()} dir="rtl">
        <h2 className="text-lg font-bold text-white mb-4">הוסף חיבור Globalmax</h2>

        <div className="space-y-4 mb-5">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">מספר נכנס (DID)</label>
            <input
              type="text"
              value={did}
              onChange={(e) => setDid(e.target.value)}
              placeholder="072-1234567"
              dir="ltr"
              className="w-full h-9 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-gold/30 num"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">אופציונלי — ניתן למלא אחר כך</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Customer ID אצל Globalmax</label>
            <input
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="gm_cust_..."
              dir="ltr"
              className="w-full h-9 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-gold/30 num"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">המזהה של הלקוח בצד של Globalmax (אופציונלי)</p>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">ביטול</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="px-5 py-2 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'יוצר...' : 'צור חיבור'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Globalmax Token Modal (post-create / post-rotate) ──────────────────────────

function GlobalmaxTokenModal({ token, onClose }: { token: string; onClose: () => void }) {
  const copy = () => {
    navigator.clipboard.writeText(token);
    toast.success('הועתק');
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4" onClick={(e) => e.stopPropagation()} dir="rtl">
        <h2 className="text-lg font-bold text-white mb-2">Token חדש נוצר</h2>
        <p className="text-xs text-slate-400 mb-4">העתק את ה-token והדבק אותו בהגדרות הלקוח ב-Globalmax תחת הספק "Jumpstart CRM".</p>
        <div className="bg-navy-dark border border-navy-border rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
          <span className="font-mono num text-xs text-white break-all">{token}</span>
          <button
            onClick={copy}
            className="flex-shrink-0 h-8 px-3 text-xs font-semibold text-gold border border-gold/30 rounded-lg hover:bg-gold/10 transition-colors flex items-center gap-1.5"
          >
            <Copy className="h-3 w-3" /> העתק
          </button>
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="px-5 py-2 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold/90 transition-colors">סיום</button>
        </div>
      </div>
    </div>
  );
}

// ── Globalmax Section ──────────────────────────────────────────────────────────

function GlobalmaxSection({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const { data: conn, isLoading } = useQuery({
    queryKey: ['globalmax_conn', orgId],
    queryFn: () => telephonyApi.getGlobalmax(orgId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['globalmax_conn', orgId] });

  const rotateMutation = useMutation({
    mutationFn: () => telephonyApi.rotateToken(conn!.id),
    onSuccess: (fresh) => { invalidate(); setNewToken(fresh.account_token ?? null); },
    onError: (e: Error) => toast.error(`שגיאה: ${e.message}`),
  });

  const toggleMutation = useMutation({
    mutationFn: (next: 'active' | 'suspended') => telephonyApi.setStatus(conn!.id, next),
    onSuccess: () => { invalidate(); toast.success('סטטוס עודכן'); },
    onError: (e: Error) => toast.error(`שגיאה: ${e.message}`),
  });

  const removeMutation = useMutation({
    mutationFn: () => telephonyApi.remove(conn!.id),
    onSuccess: () => { invalidate(); setRevealed(false); toast.success('החיבור נמחק'); },
    onError: (e: Error) => toast.error(`שגיאה: ${e.message}`),
  });

  const copyToken = () => {
    if (!conn?.account_token) return;
    navigator.clipboard.writeText(conn.account_token);
    toast.success('הועתק');
  };

  const statusKey = conn?.provisioning_status ?? (conn?.is_active ? 'pending' : 'suspended');
  const statusCfg = GLOBALMAX_STATUS[statusKey] ?? GLOBALMAX_STATUS.pending;

  return (
    <section>
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Phone className="h-4 w-4 text-gold" />
        טלפוניה — Globalmax
      </h3>

      {isLoading ? (
        <Loading text="טוען חיבור..." />
      ) : !conn ? (
        <div className="bg-navy-dark border-2 border-dashed border-navy-border rounded-xl p-8 text-center">
          <Phone className="h-10 w-10 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500 mb-4">לא הוגדר חיבור Globalmax לארגון זה</p>
          <button
            onClick={() => setShowConnect(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-gold text-navy-dark text-xs font-bold rounded-lg hover:bg-gold/90 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            הוסף חיבור Globalmax
          </button>
        </div>
      ) : (
        <div className="bg-navy-dark border border-navy-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">חיבור פעיל</span>
              <Badge variant={statusCfg.badge}>{statusCfg.label}</Badge>
            </div>
            <span className="text-[11px] text-slate-500 num">{new Date(conn.created_at).toLocaleDateString('he-IL')}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-slate-500 mb-0.5">מספר נכנס (DID)</div>
              <div className="text-white font-mono num">{conn.did_number ?? '—'}</div>
            </div>
            <div>
              <div className="text-slate-500 mb-0.5">Customer ID</div>
              <div className="text-white font-mono num">{conn.globalmax_customer_id ?? '—'}</div>
            </div>
          </div>

          <div>
            <div className="text-slate-500 text-xs mb-1">account_token</div>
            <div className="flex items-center gap-2 bg-navy-card border border-navy-border rounded-lg p-2">
              <span className="flex-1 font-mono num text-xs text-white break-all">
                {revealed ? conn.account_token ?? '—' : maskToken(conn.account_token)}
              </span>
              <button
                onClick={() => setRevealed((v) => !v)}
                title={revealed ? 'הסתר' : 'הצג'}
                className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={copyToken}
                disabled={!conn.account_token}
                title="העתק"
                className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-gold hover:bg-gold/10 transition-colors disabled:opacity-40"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => {
                if (confirm('שליפת טוקן חדש תנתק את Globalmax עד שתדביק את הטוקן החדש אצלם. להמשיך?')) {
                  rotateMutation.mutate();
                }
              }}
              disabled={rotateMutation.isPending}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-gold border border-gold/30 rounded-lg hover:bg-gold/10 transition-colors disabled:opacity-40"
            >
              <RefreshCw className="h-3 w-3" />
              רענן טוקן
            </button>
            {statusKey === 'suspended' ? (
              <button
                onClick={() => toggleMutation.mutate('active')}
                disabled={toggleMutation.isPending}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-green-400 border border-green-400/30 rounded-lg hover:bg-green-400/10 transition-colors disabled:opacity-40"
              >
                <Play className="h-3 w-3" />
                הפעל
              </button>
            ) : (
              <button
                onClick={() => toggleMutation.mutate('suspended')}
                disabled={toggleMutation.isPending}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-amber-400 border border-amber-400/30 rounded-lg hover:bg-amber-400/10 transition-colors disabled:opacity-40"
              >
                <Pause className="h-3 w-3" />
                השהה
              </button>
            )}
            <button
              onClick={() => { if (confirm('למחוק את חיבור ה-Globalmax?')) removeMutation.mutate(); }}
              disabled={removeMutation.isPending}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-red-400 border border-red-400/30 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" />
              מחק חיבור
            </button>
          </div>

          <p className="text-[11px] text-slate-500 pt-1 border-t border-navy-border/60">
            💡 העתק את ה-token להגדרות הלקוח ב-Globalmax תחת הספק "Jumpstart CRM"
          </p>
        </div>
      )}

      {showConnect && (
        <GlobalmaxConnectModal
          orgId={orgId}
          onClose={() => setShowConnect(false)}
          onCreated={(fresh) => {
            invalidate();
            if (fresh.account_token) setNewToken(fresh.account_token);
          }}
        />
      )}

      {newToken && (
        <GlobalmaxTokenModal token={newToken} onClose={() => setNewToken(null)} />
      )}
    </section>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function OrgIntegrationsTab({ orgId }: { orgId: string }) {
  const [showProvision, setShowProvision] = useState(false);
  const queryClient = useQueryClient();

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['whatsapp_accounts_admin', orgId],
    queryFn: () => integrationsApi.getWhatsappAccounts(orgId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['whatsapp_accounts_admin', orgId] });
  const sendpulseLink = integrationsApi.sendpulse.generatePartnerLink(orgId);

  return (
    <div dir="rtl" className="space-y-6">
      <OrgEmailChannelSection key={orgId} orgId={orgId} />
      {/* WhatsApp section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Wifi className="h-4 w-4 text-green-400" />
              חשבונות WhatsApp
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">מספרי GreenAPI המחוברים לארגון</p>
          </div>
          <button onClick={() => setShowProvision(true)}
            className="flex items-center gap-1.5 h-8 px-3 bg-gold text-navy-dark text-xs font-bold rounded-lg hover:bg-gold/90 transition-colors">
            <Plus className="h-3.5 w-3.5" />
            הוסף מספר
          </button>
        </div>

        {isLoading ? (
          <Loading text="טוען חשבונות..." />
        ) : !accounts?.length ? (
          <div className="bg-navy-dark border-2 border-dashed border-navy-border rounded-xl p-8 text-center">
            <Wifi className="h-10 w-10 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">אין חשבונות WhatsApp לארגון זה</p>
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((acc) => (
              <WhatsAppCard key={acc.id} account={acc} orgId={orgId} onRefresh={refresh} />
            ))}
          </div>
        )}
      </section>

      {/* SendPulse section */}
      <section>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-teal" />
          SendPulse
        </h3>
        <div className="bg-navy-dark border border-navy-border rounded-xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-white font-medium">לינק הרשמת שותף</p>
            <p className="text-xs text-slate-500 mt-0.5 font-mono num truncate max-w-[300px]">{sendpulseLink}</p>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(sendpulseLink); toast.success('הועתק'); }}
            className="h-8 px-3 text-xs font-semibold text-teal border border-teal/30 rounded-lg hover:bg-teal/10 transition-colors flex-shrink-0">
            העתק
          </button>
        </div>
      </section>

      {/* Globalmax telephony section */}
      <GlobalmaxSection orgId={orgId} />

      {showProvision && <ProvisionModal orgId={orgId} onClose={() => setShowProvision(false)} onDone={refresh} />}
    </div>
  );
}
